import {noop} from 'lodash';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {FrontEvent, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {StyledTabBaseElement} from './tabCommon';

/*
 * Props.
 */

export interface TabButtonElementProps {
  name: ReactNode;
  isSelected: boolean;
  isDisabled?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;

  onTabClick: HandlerOf<FrontEvent | undefined>;

  closeLabel?: ReactNode;
  onCloseClick?: HandlerOf<FrontEvent | undefined>;
  interactionId?: string;
}

/*
 * Styles.
 */

const StyledIconButton = styled(IconButton)`
  padding: 0px;
  margin-left: 4px;

  &:hover {
    background: none;
  }
`;

const StyledNameWrapper = styled.div`
  ${ellipsis()}
`;

const StyledLeftIcon = styled.div`
  margin-right: 6px;
`;

const StyledRightIcon = styled.div`
  margin-left: 6px;
`;

/*
 * Component.
 */

export const TabButtonElement: FC<TabButtonElementProps> = ({
  name,
  isSelected,
  isDisabled,
  leftIcon,
  rightIcon,
  onTabClick,
  closeLabel,
  onCloseClick,
  interactionId,
}) => (
  <StyledTabBaseElement
    onClick={isDisabled ? noop : onTabClick}
    $isSelected={isSelected}
    $isDisabled={isDisabled}
    interactionId={interactionId}
  >
    {leftIcon ? <StyledLeftIcon>{leftIcon}</StyledLeftIcon> : null}
    <StyledNameWrapper>{name}</StyledNameWrapper>
    {maybeRenderCloseButton(onCloseClick, closeLabel)}
    {rightIcon ? <StyledRightIcon>{rightIcon}</StyledRightIcon> : null}
  </StyledTabBaseElement>
);

/*
 * Helpers.
 */

const maybeRenderCloseButton = (onCloseClick?: HandlerOf<FrontEvent | undefined>, closeLabel?: ReactNode) => {
  if (!onCloseClick) {
    return null;
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledIconButton
          iconName="closeTiny"
          isRounded
          onClick={onCloseClick}
          hoverColor={theme.greys.shade80}
          label={closeLabel || <Trans id="tab-close-default">Close</Trans>}
        />
      )}
    </UserThemeConsumer>
  );
};
